import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  fetchBvProviders,
  checkProviderStatus,
  getProviderAuthorizeUrl,
  disconnectProvider,
  BvProvider,
} from "../../../../redux/transactSlice";
import { useToastMessage } from "../../../../components/ToastMessage/ToastMessage";
import { simpleTableCellStyle as cellStyle } from "../../../../components/Common/simpleTableCellStyle";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Spinner,
  Toaster,
  tokens,
} from "@fluentui/react-components";

// Integrations -> Bulk Valuation (WAT-I2726). Provider-list-driven: the rows
// come from masterdata's GET providers endpoint (every registered OAuth
// strategy), not a hardcoded Transact row — adding a future OAuth-based BV
// provider server-side is enough for it to show up here automatically.
//
// OAuth completion is detected by polling getStatus, NOT postMessage — the
// only proven precedent in this codebase (CashCalcPage.tsx) uses popup +
// polling; postMessage does not appear anywhere in this frontend's OAuth
// code. See BV_TRANSACT_STEP1_ARCHITECTURE.md §8.5 for the correction.
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // matches the design doc's ~5 minute abandoned-attempt timeout

function statusLabel(status: string): string {
  switch (status) {
    case "CONNECTED":
      return "Connected";
    case "AUTHORISATION_PENDING":
      return "Authorising…";
    case "REAUTHORISATION_REQUIRED":
      return "Reauthorisation Required";
    case "TOKEN_REFRESH_REQUIRED":
      return "Refreshing…";
    case "ERROR":
      return "Failed";
    default:
      return "Not Connected";
  }
}

function statusColor(status: string): "success" | "warning" | "danger" | "informative" {
  switch (status) {
    case "CONNECTED":
      return "success";
    case "AUTHORISATION_PENDING":
    case "TOKEN_REFRESH_REQUIRED":
      return "informative";
    case "REAUTHORISATION_REQUIRED":
    case "ERROR":
      return "danger";
    default:
      return "warning";
  }
}

function fmtDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

const TransactPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast: notify, toasterId } = useToastMessage();

  const { providers, listLoading } = useSelector((state: RootState) => state.transact);

  // Keyed by provider code — only one row is ever "busy" at a time in
  // practice, but this keeps each row's own Connect button state isolated.
  const [connectingCode, setConnectingCode] = useState<string | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<BvProvider | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards handleConnect's two awaited dispatches (getProviderAuthorizeUrl,
  // then every checkProviderStatus poll tick) against running after this
  // component has unmounted — the cleanup below runs synchronously on
  // unmount, before either await can see it, so a stale response must check
  // this directly instead.
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    dispatch(fetchBvProviders()).then((result) => {
      if (fetchBvProviders.rejected.match(result)) {
        notify("Error", (result.payload as string) || "Unable to load integration providers.", "error");
      }
    });
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, [dispatch]);

  const stopPolling = () => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
    setConnectingCode(null);
  };

  const handleConnect = async (provider: BvProvider) => {
    // Open the popup synchronously within the click handler, BEFORE any
    // network call — the same reason CashCalcPage.tsx does this — so popup
    // blockers never intervene (BV_TRANSACT_STEP1_ARCHITECTURE.md §8.2).
    const popup = window.open("about:blank", `${provider.code}Auth`, "width=520,height=680");
    if (!popup) {
      notify("Pop-up Blocked", "Allow pop-ups for this site, then select Connect again.", "error");
      return;
    }

    setConnectingCode(provider.code);
    const result = await dispatch(getProviderAuthorizeUrl(provider.code));
    if (!isMountedRef.current) {
      // The page navigated away while this request was in flight — the
      // cleanup effect already ran (with nothing yet to clear), so this is
      // the only place left to stop before an interval/timeout gets armed
      // on a component that's gone.
      try { popup.close(); } catch { /* ignore */ }
      return;
    }
    if (!getProviderAuthorizeUrl.fulfilled.match(result) || !result.payload) {
      popup.close();
      setConnectingCode(null);
      notify("Connection Failed", (result as any).payload || `Could not start the ${provider.name} connection.`, "error");
      return;
    }

    popup.location.href = result.payload;

    let settled = false;
    // Guards against a tick starting a new checkProviderStatus call while a
    // previous tick's call is still in flight (e.g. the request is slower
    // than POLL_INTERVAL_MS) — without it, two overlapping "popup closed"
    // branches could both resolve afterwards, each firing its own toast.
    let requestInFlight = false;
    pollIntervalRef.current = setInterval(async () => {
      if (settled || requestInFlight) return;
      if (popup.closed) {
        // Popup closed without the poll having already seen CONNECTED — resolve
        // once more via status in case postMessage-less completion raced the close.
        requestInFlight = true;
        const statusResult = await dispatch(checkProviderStatus(provider.code));
        requestInFlight = false;
        if (settled || !isMountedRef.current) return;
        settled = true;
        stopPolling();
        if (checkProviderStatus.fulfilled.match(statusResult) && statusResult.payload.status === "CONNECTED") {
          notify("Connected", `Successfully connected to ${provider.name}.`);
        } else if (checkProviderStatus.fulfilled.match(statusResult)) {
          notify("Not Connected", "The sign-in window was closed before sign-in finished.", "warning");
        } else {
          // The status check itself failed (network blip, 5xx) — we genuinely
          // don't know whether sign-in finished, so don't claim it didn't.
          notify("Status Unknown", "Could not confirm the connection status. Refresh the page to check again.", "warning");
        }
        return;
      }
      requestInFlight = true;
      const statusResult = await dispatch(checkProviderStatus(provider.code));
      requestInFlight = false;
      if (checkProviderStatus.fulfilled.match(statusResult) && statusResult.payload.status === "CONNECTED" && !settled) {
        settled = true;
        stopPolling();
        try { popup.close(); } catch { /* ignore */ }
        notify("Connected", `Successfully connected to ${provider.name}.`);
      }
    }, POLL_INTERVAL_MS);

    pollTimeoutRef.current = setTimeout(() => {
      if (settled) return;
      settled = true;
      stopPolling();
      try { popup.close(); } catch { /* ignore */ }
      notify("Timed Out", "The sign-in timed out. Select Connect to try again.", "warning");
    }, POLL_TIMEOUT_MS);
  };

  const handleDisconnect = async () => {
    if (!disconnectTarget || disconnecting) return;
    const provider = disconnectTarget;
    setDisconnecting(true);
    const result = await dispatch(disconnectProvider(provider.code));
    setDisconnecting(false);
    setDisconnectTarget(null);
    if (disconnectProvider.fulfilled.match(result)) {
      notify("Disconnected", `${provider.name} disconnected.`);
    } else {
      notify("Error", (result as any).payload || `Failed to disconnect ${provider.name}.`, "error");
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />

      <div style={{ marginBottom: "16px" }}>
        <span style={{ fontSize: "18px", fontWeight: 600 }}>Bulk Valuation</span>
        <p style={{ fontSize: "13px", color: tokens.colorNeutralForeground3, marginTop: "4px", maxWidth: "640px" }}>
          Connect a provider to enable automated bulk valuation retrieval. Connecting signs in with the
          provider and stores a refresh token used for future valuation requests.
        </p>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={cellStyle(true, false, true)}>Provider</th>
              <th style={cellStyle(false, false, true)}>Status</th>
              <th style={cellStyle(false, false, true)}>Last Connected</th>
              <th style={cellStyle(false, false, true)}>Expiry Date</th>
              <th style={cellStyle(false, true, true)}>Action</th>
            </tr>
          </thead>
          <tbody>
            {listLoading && providers.length === 0 ? (
              <tr>
                <td style={cellStyle(true, true)} colSpan={5}>
                  <Spinner size="tiny" label="Loading providers…" labelPosition="after" />
                </td>
              </tr>
            ) : providers.length === 0 ? (
              <tr>
                <td style={cellStyle(true, true)} colSpan={5}>
                  No integration providers are available.
                </td>
              </tr>
            ) : (
              providers.map((provider) => {
                const connected = provider.status === "CONNECTED";
                const isBusy = connectingCode === provider.code;
                return (
                  <tr key={provider.code}>
                    <td style={cellStyle(true, false)}><strong>{provider.name}</strong></td>
                    <td style={cellStyle(false, false)}>
                      <Badge color={statusColor(provider.status)} size="medium">{statusLabel(provider.status)}</Badge>
                    </td>
                    <td style={cellStyle(false, false)}>{fmtDate(provider.lastConnected)}</td>
                    <td style={cellStyle(false, false)}>{fmtDate(provider.expiryDate)}</td>
                    <td style={cellStyle(false, true)}>
                      {connected ? (
                        <Button
                          appearance="secondary"
                          className="asc-button-secondary"
                          style={{ color: "red" }}
                          onClick={() => setDisconnectTarget(provider)}
                        >
                          Disconnect
                        </Button>
                      ) : (
                        <Button
                          appearance="primary"
                          className="asc-button-primary"
                          shape="rounded"
                          size="medium"
                          onClick={() => handleConnect(provider)}
                          disabled={connectingCode !== null}
                          icon={isBusy ? <Spinner size="tiny" /> : undefined}
                        >
                          {isBusy ? "Connecting…" : provider.status === "REAUTHORISATION_REQUIRED" ? "Reconnect" : "Connect"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={disconnectTarget !== null} onOpenChange={(_, d) => { if (!d.open) setDisconnectTarget(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Disconnect {disconnectTarget?.name}</DialogTitle>
            <DialogContent>
              Are you sure you want to disconnect {disconnectTarget?.name}? Automated bulk valuation retrieval
              for this tenant will stop until it's reconnected.
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleDisconnect}
                disabled={disconnecting}
                style={{ backgroundColor: "#d13438", borderColor: "#d13438" }}
              >
                Disconnect
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default TransactPage;
